import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import adminApi from "@/lib/adminApi";
import { PageHeader, AdminCard, StatCard, PageLoader } from "@/admin/components";
import { formatINR } from "@/lib/format";

const currentMonth = () => new Date().toISOString().slice(0, 7);

const METHOD_LABEL = { upi: "UPI", bank: "Bank Transfer", cash: "Cash", razorpay: "Razorpay" };

export default function Revenue() {
  const [tab, setTab] = useState("consultation");
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [refunds, setRefunds] = useState(null);
  const [refundMonth, setRefundMonth] = useState(currentMonth());
  const [refundsLoading, setRefundsLoading] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/revenue/consultation").then((r) => setData(r.data));
    adminApi.get("/admin/overview").then((r) => setOverview(r.data));
  }, []);

  useEffect(() => {
    if (tab !== "consultation") return;
    setRefundsLoading(true);
    setRefunds(null);
    adminApi.get("/admin/revenue/refunds", { params: { month: refundMonth } })
      .then((r) => setRefunds(r.data.refunds))
      .finally(() => setRefundsLoading(false));
  }, [tab, refundMonth]);

  return (
    <div data-testid="admin-revenue-page">
      <PageHeader title="Revenue" />

      <div className="mb-6 flex gap-2">
        {[["consultation", "Consultation Revenue"], ["subscription", "Subscription Revenue"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`revenue-tab-${k}`}
            onClick={() => setTab(k)}
            className={`h-10 rounded-full px-5 text-[14px] font-medium transition-colors ${tab === k ? "bg-[#111111] text-white" : "bg-[#f2f0ec] text-[#666]"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "consultation" ? (
        !data ? <PageLoader /> : (
          <div data-testid="consultation-tab">
            <div className="grid grid-cols-4 gap-4">
              <StatCard label="Gross · all time" value={formatINR(data.stats.gross_all)} />
              <StatCard label="Gross · this month" value={formatINR(data.stats.gross_month)} />
              <StatCard label="Refunds · this month" value={formatINR(data.stats.refunds_month)} />
              <StatCard label="Failed · this month" value={data.stats.failed_month} />
            </div>

            {/* Platform earnings & tax pots */}
            {data.pots && (
              <>
                <h3 className="mb-3 mt-8 text-[13px] font-semibold uppercase tracking-[0.08em] text-[#999]">
                  Platform earnings &amp; tax pots
                </h3>
                <div className="grid grid-cols-4 gap-4" data-testid="revenue-pots">
                  <StatCard testid="pot-commission-month" label={`Commission · this month (${data.pots.platform_fee_percent}%)`}
                    value={formatINR(data.pots.commission_month)} hint={`All time ${formatINR(data.pots.commission_all)}`} />
                  <StatCard testid="pot-gst-month" label={`GST / TCS pot · this month (${data.pots.tcs_percent}%)`}
                    value={formatINR(data.pots.tcs_month)} hint={`All time ${formatINR(data.pots.tcs_all)}`} />
                  <StatCard testid="pot-net-month" label="Paid to astrologers · this month"
                    value={formatINR(data.pots.net_month)} hint={`All time ${formatINR(data.pots.net_all)}`} />
                  <StatCard testid="pot-collected-month" label="Total platform collected · this month"
                    value={formatINR(data.pots.commission_month + data.pots.tcs_month)}
                    hint={`All time ${formatINR(data.pots.commission_all + data.pots.tcs_all)}`} />
                </div>
              </>
            )}

            <AdminCard className="mt-6 p-6">
              <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Monthly consultation revenue · last 12 months</h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#999" }} tickLine={false} axisLine={{ stroke: "#e9e6e1" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#999" }} tickLine={false} axisLine={false} width={48} />
                    <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e9e6e1", fontSize: 13 }} cursor={{ fill: "#f7f5f1" }} />
                    <Bar dataKey="amount" fill="#111111" radius={[6, 6, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </AdminCard>

            {/* Monthly breakdown for GST filing */}
            {data.monthly_pots && (
              <AdminCard className="mt-6 p-6" testid="revenue-pots-table">
                <h3 className="mb-1 text-[15px] font-semibold text-[#111]">Monthly tax &amp; commission breakdown</h3>
                <p className="mb-4 text-[13px] text-[#999]">Use the GST / TCS pot column to file GST each month.</p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-[14px]">
                    <thead>
                      <tr className="border-b border-[#e9e6e1] text-[11px] uppercase tracking-[0.06em] text-[#999]">
                        <th className="px-3 py-2.5 font-semibold">Month</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Gross</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Commission ({data.pots?.platform_fee_percent}%)</th>
                        <th className="px-3 py-2.5 text-right font-semibold">GST / TCS pot ({data.pots?.tcs_percent}%)</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Paid to astrologers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.monthly_pots].reverse().map((m) => (
                        <tr key={m.month} className="border-b border-[#f2efea] text-[#222]">
                          <td className="px-3 py-3 font-medium">{m.month}</td>
                          <td className="px-3 py-3 text-right">{formatINR(m.gross)}</td>
                          <td className="px-3 py-3 text-right">{formatINR(m.platform_fee)}</td>
                          <td className="px-3 py-3 text-right font-semibold text-[#111]">{formatINR(m.tcs)}</td>
                          <td className="px-3 py-3 text-right text-[#666]">{formatINR(m.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminCard>
            )}

            {/* Refunds table */}
            <AdminCard className="mt-6 p-6" data-testid="revenue-refunds-section">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-[15px] font-semibold text-[#111]">Refunds this month</h3>
                  <p className="mt-0.5 text-[13px] text-[#999]">Manual and Razorpay refunds recorded</p>
                </div>
                <input
                  type="month"
                  data-testid="refund-month-filter"
                  value={refundMonth}
                  onChange={(e) => setRefundMonth(e.target.value)}
                  className="h-9 rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[13px] text-[#111] outline-none focus:border-[#111]"
                />
              </div>

              {refundsLoading ? (
                <div className="py-8 text-center text-[13px] text-[#999]">Loading…</div>
              ) : !refunds || refunds.length === 0 ? (
                <p className="py-6 text-center text-[13px] text-[#999]">No refunds for this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-[14px]" data-testid="refunds-table">
                    <thead>
                      <tr className="border-b border-[#e9e6e1] text-[11px] uppercase tracking-[0.06em] text-[#999]">
                        <th className="px-3 py-2.5 font-semibold">Booking Ref</th>
                        <th className="px-3 py-2.5 font-semibold">Astrologer</th>
                        <th className="px-3 py-2.5 font-semibold">Client</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Gross</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Refund</th>
                        <th className="px-3 py-2.5 font-semibold">Method</th>
                        <th className="px-3 py-2.5 font-semibold">Reference</th>
                        <th className="px-3 py-2.5 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refunds.map((r, i) => (
                        <tr key={i} className="border-b border-[#f2efea] text-[#222]">
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[12px]">#{r.booking_reference}</span>
                              {r.is_manual && (
                                <span className="rounded-[4px] bg-[#111] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Manual</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-[#555]">{r.astrologer_name || "—"}</td>
                          <td className="px-3 py-3 text-[#555]">{r.client_name || "—"}</td>
                          <td className="px-3 py-3 text-right text-[#555]">{formatINR(r.gross_amount_inr)}</td>
                          <td className="px-3 py-3 text-right font-semibold text-[#c0392b]">{formatINR(r.refund_amount_inr)}</td>
                          <td className="px-3 py-3 text-[#555]">{METHOD_LABEL[r.method] || r.method || "—"}</td>
                          <td className="px-3 py-3">
                            {r.reference ? (
                              <span title={r.reference} className="cursor-default font-mono text-[12px] text-[#555]">
                                {r.reference.slice(0, 12)}{r.reference.length > 12 ? "…" : ""}
                              </span>
                            ) : <span className="text-[#bbb]">—</span>}
                          </td>
                          <td className="px-3 py-3 text-[#999] text-[12px]">
                            {r.refunded_at ? r.refunded_at.slice(0, 10) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AdminCard>
          </div>
        )
      ) : (
        <div data-testid="subscription-tab">
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Active Subscriptions" value={overview ? overview.stats.active_paid.value : "—"} />
            <StatCard label="MRR" value={overview ? formatINR(overview.stats.mrr.value) : "—"} />
            <StatCard label="New · this month" value="—" />
            <StatCard label="Cancellations · month" value={overview ? overview.stats.churned.value : "—"} />
          </div>
          <AdminCard className="mt-6 p-8 text-center">
            <p className="text-[15px] font-semibold text-[#111]">Recurring subscription analytics activate when Razorpay AutoPay goes live</p>
            <p className="mx-auto mt-2 max-w-[460px] text-[14px] text-[#999]">
              Upcoming renewals, failed payments and detailed cancellation history will populate here once recurring
              subscriptions are processing real charges. Current active count &amp; MRR above reflect manual/lifetime activations.
            </p>
          </AdminCard>
        </div>
      )}
    </div>
  );
}
